import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import I18nText from "../components/I18nText";
import CharlesStory from "../components/employee/CharlesStory";
import { PROJECTED_GROWTH_RATE, HORIZON_AGES, SHARE_MAX_FRIEND_RECIPIENTS } from "../config/constants";
import { track } from "../lib/analytics";
import { isValidEmail, titleCaseName } from "../lib/dataHygiene";
import {
    submitEmployeeInterest,
    submitContact,
    shareFriend,
    resolveInvitation,
    expressInterest,
    // Phase 3d — `joinMarketingWaitlist` removed from imports here. The
    // `/employees` card now uses `sendMeTheGoodStuff` instead. The old
    // endpoint still exists in lib/api.js for backward-compat with any
    // other surfaces that may reference it.
    sendMeTheGoodStuff,
    formatApiError,
    badRecipientIndices,
    API,
} from "../lib/api";
import axios from "axios";

const fmt$ = (n) => "$" + Math.round(n).toLocaleString();
const fmt$2 = (n) => "$" + n.toFixed(2);

function projectCashValue(cashAt65, targetAge) {
    const years = targetAge - 65;
    if (years <= 0) return cashAt65;
    return cashAt65 * Math.pow(1 + PROJECTED_GROWTH_RATE, years);
}

function captionForKey(premium) {
    if (premium < 20) return "under20";
    if (premium < 40) return "under40";
    if (premium < 75) return "under75";
    if (premium < 120) return "under120";
    return "more";
}

function horizonData(horizon, cashValue65, t) {
    const key = `${horizon}`;
    return {
        cashLabel: t(`employees.calculator.cashAt${key}Label`),
        cashValue:
            horizon === 65
                ? cashValue65
                : projectCashValue(cashValue65, horizon),
        cashCaption: t(`employees.calculator.cashAt${key}Caption`),
        dbCaption: t(`employees.calculator.dbCaption${key}`),
        isProjection: horizon !== 65,
    };
}

function NotifyMePanel({ calcState, onDone }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [err, setErr] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!isValidEmail(email.trim())) {
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        track("employee_notify_submit", { ...calcState });
        setSending(true);
        try {
            await submitEmployeeInterest({ email: email.trim(), calculatorState: calcState });
            track("employee_notify_success", {});
            onDone();
        } catch (e2) {
            setErr(formatApiError(e2) || t("errors.generic"));
        } finally {
            setSending(false);
        }
    };

    return (
        <form className="ladder-inline" onSubmit={submit} data-testid="notify-form">
            <div className="row">
                <label>{t("employees.finalCta.notifyFormEmailLabel")}</label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={(e) => setEmail(e.target.value.trim())}
                    placeholder={t("employees.finalCta.notifyFormEmailPlaceholder")}
                    data-testid="notify-email"
                />
            </div>
            {err && <div className="err" data-testid="notify-err">{err}</div>}
            <button type="submit" className="btn-submit" disabled={sending} data-testid="notify-submit">
                {sending ? t("employees.finalCta.submitting") : t("employees.finalCta.notifySubmit")}
            </button>
        </form>
    );
}

function AskHumanPanel({ onDone }) {
    const { t } = useTranslation();
    const [values, setValues] = useState({ name: "", email: "", message: "" });
    const [err, setErr] = useState("");
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!values.name.trim() || !values.email.trim() || !values.message.trim()) {
            setErr(t("errors.allRequired"));
            return;
        }
        if (!isValidEmail(values.email.trim())) {
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        track("contact_submit", {});
        setSending(true);
        try {
            await submitContact({
                name: values.name.trim(),
                email: values.email.trim(),
                message: values.message.trim(),
            });
            track("contact_success", {});
            onDone();
        } catch (e2) {
            setErr(formatApiError(e2) || t("errors.generic"));
        } finally {
            setSending(false);
        }
    };

    return (
        <form className="ladder-inline" onSubmit={submit} data-testid="contact-form">
            <div className="row">
                <label>{t("employees.finalCta.humanFormNameLabel")}</label>
                <input type="text" value={values.name} onChange={set("name")}
                       onBlur={(e) => setValues((v) => ({ ...v, name: titleCaseName(e.target.value) }))}
                       placeholder={t("employees.finalCta.humanFormNamePlaceholder")}
                       data-testid="contact-name" />
            </div>
            <div className="row">
                <label>{t("employees.finalCta.humanFormEmailLabel")}</label>
                <input type="email" value={values.email} onChange={set("email")}
                       onBlur={(e) => setValues((v) => ({ ...v, email: e.target.value.trim() }))}
                       placeholder={t("employees.finalCta.humanFormEmailPlaceholder")}
                       data-testid="contact-email" />
            </div>
            <div className="row">
                <label>{t("employees.finalCta.humanFormMessageLabel")}</label>
                <textarea rows={3} value={values.message} onChange={set("message")}
                          placeholder={t("employees.finalCta.humanFormMessagePlaceholder")}
                          data-testid="contact-message" />
            </div>
            {err && <div className="err" data-testid="contact-err">{err}</div>}
            <button type="submit" className="btn-submit" disabled={sending} data-testid="contact-submit">
                {sending ? t("employees.finalCta.submitting") : t("employees.finalCta.humanSubmit")}
            </button>
        </form>
    );
}

function Calculator({ invited }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [age, setAge] = useState(invited?.age || 35);
    const [coverage, setCoverage] = useState(50000);
    const [tobacco, setTobacco] = useState(false);
    const [horizon, setHorizon] = useState(65);
    const [ratesMap, setRatesMap] = useState(null);

    // When the invitation resolves, lock the age to the one from the census.
    useEffect(() => {
        if (invited?.age) setAge(invited.age);
    }, [invited?.age]);

    // Fetch canonical MM rates once on mount. Key: `${age}|${tier}|${class}`.
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/rates`).then((r) => {
            if (cancelled) return;
            const map = {};
            for (const row of r.data.rows || []) {
                const key = `${row.age}|${row.coverage_tier}|${row.classification}`;
                map[key] = row;
            }
            setRatesMap(map);
        }).catch(() => {
            // If /api/rates is unavailable, fall back to prototype numbers.
            setRatesMap({});
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const { premium, h } = useMemo(() => {
        if (!ratesMap) return { premium: null, h: null };
        const cls = tobacco ? "tobacco" : "non_tobacco";
        const key = `${age}|${coverage}|${cls}`;
        const row = ratesMap[key];
        if (!row) {
            return { premium: null, h: null };
        }
        // MM's published `guaranteed_cash_value` for the anchor row is the
        // value at the milestone (age 65). We treat it as cv@65 and project
        // outward to the other horizons using PROJECTED_GROWTH_RATE.
        const cv65 = row.guaranteed_cash_value || 0;
        return {
            premium: row.bi_weekly_premium,
            h: horizonData(horizon, cv65, t),
        };
    }, [age, coverage, tobacco, horizon, ratesMap, t]);

    useEffect(() => {
        track("calculator_interaction", { age, coverage, tobacco, horizon });
    }, [age, coverage, tobacco, horizon]);

    // While the canonical rates are loading, show the old prototype number as
    // a harmless placeholder so the page doesn't flash empty.
    if (premium === null || !h) {
        return (
            <div className="calculator" data-testid="calculator-loading">
                <div className="calc-left">
                    <h3>{t("employees.calculator.title")}</h3>
                    <p className="calc-sub">{t("employees.calculator.subtitle")}</p>
                </div>
                <div className="calc-right">
                    <div className="result-block">
                        <div className="result-label mono">{t("employees.calculator.costLabel")}</div>
                        <div className="result-value premium">…</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="calculator" data-testid="calculator">
                <div className="calc-left">
                    <h3>{t("employees.calculator.title")}</h3>
                    <p className="calc-sub">{t("employees.calculator.subtitle")}</p>

                    <div className="slider-group">
                        <div className="slider-row">
                            <span className="slider-label mono">{t("employees.calculator.ageLabel")}</span>
                            <span className="slider-value" data-testid="age-display">
                                {age}
                                {invited?.age ? (
                                    <span className="age-lock-chip mono" data-testid="age-lock-chip">
                                        {t("employees.invited.ageLockChip")}
                                    </span>
                                ) : null}
                            </span>
                        </div>
                        {invited?.age ? (
                            <div
                                className="age-locked-bar"
                                data-testid="age-slider-locked"
                                aria-label={t("employees.invited.ageLockedAria")}
                            />
                        ) : (
                            <input type="range" min="18" max="75" value={age}
                                   onChange={(e) => setAge(parseInt(e.target.value, 10))}
                                   data-testid="age-slider" aria-label={t("employees.calculator.ageAria")} />
                        )}
                    </div>

                    <div className="slider-group">
                        <div className="slider-row">
                            <span className="slider-label mono">{t("employees.calculator.coverageLabel")}</span>
                            <span className="slider-value" data-testid="coverage-display">{fmt$(coverage)}</span>
                        </div>
                        <div className="coverage-pills">
                            {[25000, 50000, 75000, 100000].map((c) => (
                                <button type="button" key={c}
                                        className={`pill${coverage === c ? " active" : ""}`}
                                        onClick={() => setCoverage(c)}
                                        data-testid={`coverage-${c}`}>
                                    ${c / 1000}K
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="tobacco-toggle">
                        <label htmlFor="tobacco-switch">{t("employees.calculator.tobaccoLabel")}</label>
                        <button type="button" id="tobacco-switch"
                                className={`switch${tobacco ? " on" : ""}`}
                                onClick={() => setTobacco((x) => !x)} aria-pressed={tobacco}
                                data-testid="tobacco-switch">
                            <span className="switch-thumb" />
                        </button>
                    </div>
                </div>

                <div className="calc-right">
                    <div>
                        <div className="result-block">
                            <div className="result-label mono">
                                {invited ? t("employees.invited.costLabel") : t("employees.calculator.costLabel")}
                            </div>
                            <div className="result-value premium" data-testid="premium-result">
                                {fmt$2(premium)}
                                <span className="unit" data-testid="premium-unit">
                                    {" " + t("employees.calculator.cadenceUnit")}
                                </span>
                            </div>
                            <div className="result-caption" data-testid="premium-caption">
                                {t(`employees.calculator.premiumCaptions.${captionForKey(premium)}`)}
                            </div>
                        </div>

                        <div className="horizon-toggle">
                            <div className="result-label mono" style={{ marginBottom: "0.75rem" }}>
                                {t("employees.calculator.showValueAt")}
                            </div>
                            <div className="horizon-pills">
                                {HORIZON_AGES.map((hh) => (
                                    <button type="button" key={hh}
                                            className={`h-pill${horizon === hh ? " active" : ""}`}
                                            onClick={() => setHorizon(hh)}
                                            data-testid={`horizon-${hh}`}>
                                        {t(`employees.calculator.horizon.${hh}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="result-block">
                            <div className="result-label mono" data-testid="cash-label">{h.cashLabel}</div>
                            <div className="result-value" data-testid="cash-result">{fmt$(h.cashValue)}</div>
                            <div className="result-caption">{h.cashCaption}</div>
                        </div>

                        <div className="result-block">
                            <div className="result-label mono">{t("employees.calculator.dbLabel")}</div>
                            <div className="result-value" data-testid="db-result">{fmt$(coverage)}</div>
                            <div className="result-caption">{h.dbCaption}</div>
                        </div>
                    </div>

                    <div className="calc-cta">
                        {/* Phase 3d follow-up — the calculator's primary
                            CTA now navigates to the canonical employee
                            email-lookup surface at `/apply`
                            ("What's your email?" page) instead of
                            toggling to a local "thanks" state. The
                            in-place enrollSent pattern was dropped
                            because it was a dead-end that required the
                            user to find a second surface to act; the
                            card already carries the full calculator
                            context into the decision. */}
                        <button type="button"
                                className="btn-enroll"
                                onClick={() => {
                                    track("calc_enroll_cta_click", {
                                        age, coverage, tobacco, horizon,
                                        path: "calculator_iwantthis",
                                    });
                                    navigate("/apply");
                                }}
                                data-testid="calc-enroll">
                            {t("employees.calculator.enrollCta")}
                        </button>
                    </div>
                </div>
            </div>

            {h.isProjection && (
                <div className="projection-note" data-testid="projection-note">
                    <span className="mono">*</span>
                    {t("employees.calculator.projectionNote")}
                </div>
            )}
        </>
    );
}

function FinalCTA({ invited, onStartApplication }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [panel, setPanel] = useState(null);
    const calcForPayload = { age: invited?.age || 35, coverage: 50000, tobacco: false };
    const pick = (which) => () => {
        track("apply_path_selected", { path: which });
        setPanel(which);
    };
    const goApply = () => {
        track("apply_path_selected", { path: "apply" });
        navigate("/apply");
    };
    const goStartApp = () => {
        track("apply_path_selected", { path: "invited_start_application" });
        onStartApplication?.();
    };

    if (invited) {
        // Invited variant — primary becomes "Start my application", drops
        // the uninvited Sign in, swaps notify for "Come back later".
        return (
            <div className="final-cta invited" data-testid="final-cta-invited">
                <div className="section-label mono" style={{ justifyContent: "center", marginBottom: "1.5rem" }}>
                    {t("employees.invited.yourMove.sectionLabel")}
                </div>
                <h3>
                    <I18nText
                        i18nKey="employees.invited.yourMove.h3"
                        values={{ firstName: invited.first_name || "" }}
                    />
                </h3>
                <p>
                    <I18nText
                        i18nKey="employees.invited.yourMove.body"
                        values={{ employer: invited.employer_name || "" }}
                    />
                </p>

                <div className="ladder">
                    <button type="button" className="btn-ladder btn-ladder-primary"
                            onClick={goStartApp} data-testid="ladder-start-application">
                        <div className="ladder-label mono">{t("employees.invited.yourMove.applyLabel")}</div>
                        <div className="ladder-text">{t("employees.invited.yourMove.applyTitle")}</div>
                        <div className="ladder-sub">{t("employees.invited.yourMove.applySub")}</div>
                    </button>

                    <button type="button" className="btn-ladder btn-ladder-secondary"
                            data-testid="ladder-come-back-later"
                            onClick={() => {
                                track("invited_come_back_later_click", {});
                                setPanel("later");
                            }}>
                        <div className="ladder-label mono">{t("employees.invited.yourMove.laterLabel")}</div>
                        <div className="ladder-text">{t("employees.invited.yourMove.laterTitle")}</div>
                        <div className="ladder-sub">{t("employees.invited.yourMove.laterSub")}</div>
                    </button>
                    {panel === "later" && (
                        <div className="ladder-inline" data-testid="later-info">
                            <div className="mono" style={{ color: "var(--accent)", letterSpacing: "0.12em" }}>
                                {t("employees.invited.yourMove.laterStatus")}
                            </div>
                            <div style={{ marginTop: "0.5rem" }}>
                                {t("employees.invited.yourMove.laterBody")}
                            </div>
                        </div>
                    )}

                    <button type="button" className="btn-ladder btn-ladder-tertiary"
                            onClick={pick("human")} data-testid="ladder-human">
                        <div className="ladder-text-sm">
                            <I18nText i18nKey="employees.finalCta.humanTrigger" />
                        </div>
                    </button>
                    {panel === "human" && <AskHumanPanel onDone={() => setPanel("human-done")} />}
                    {panel === "human-done" && (
                        <div className="ladder-inline" data-testid="human-done" style={{ borderColor: "var(--accent)" }}>
                            <div className="mono" style={{ color: "var(--accent)", letterSpacing: "0.12em" }}>
                                {t("employees.finalCta.humanDoneStatus")}
                            </div>
                            <div style={{ marginTop: "0.5rem" }}>{t("employees.finalCta.humanDoneBody")}</div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="final-cta" data-testid="final-cta">
            <div className="section-label mono" style={{ justifyContent: "center", marginBottom: "1.5rem" }}>
                {t("employees.finalCta.sectionLabel")}
            </div>
            <h3>{t("employees.finalCta.h3")}</h3>
            <p>{t("employees.finalCta.body")}</p>

            <div className="ladder">
                <button type="button" className="btn-ladder btn-ladder-primary"
                        onClick={goApply} data-testid="ladder-apply">
                    <div className="ladder-label mono">{t("employees.finalCta.applyLabel")}</div>
                    <div className="ladder-text">{t("employees.finalCta.applyTitle")}</div>
                    <div className="ladder-sub">{t("employees.finalCta.applySub")}</div>
                </button>

                <button type="button" className="btn-ladder btn-ladder-secondary"
                        onClick={pick("notify")} data-testid="ladder-notify">
                    <div className="ladder-label mono">{t("employees.finalCta.notifyLabel")}</div>
                    <div className="ladder-text">{t("employees.finalCta.notifyTitle")}</div>
                    <div className="ladder-sub">{t("employees.finalCta.notifySub")}</div>
                </button>
                {panel === "notify" && <NotifyMePanel calcState={calcForPayload} onDone={() => setPanel("notify-done")} />}
                {panel === "notify-done" && (
                    <div className="ladder-inline" data-testid="notify-done" style={{ borderColor: "var(--accent)" }}>
                        <div className="mono" style={{ color: "var(--accent)", letterSpacing: "0.12em" }}>
                            {t("employees.finalCta.notifyDoneStatus")}
                        </div>
                        <div style={{ marginTop: "0.5rem" }}>{t("employees.finalCta.notifyDoneBody")}</div>
                    </div>
                )}

                <button type="button" className="btn-ladder btn-ladder-tertiary"
                        onClick={pick("human")} data-testid="ladder-human">
                    <div className="ladder-text-sm">
                        <I18nText i18nKey="employees.finalCta.humanTrigger" />
                    </div>
                </button>
                {panel === "human" && <AskHumanPanel onDone={() => setPanel("human-done")} />}
                {panel === "human-done" && (
                    <div className="ladder-inline" data-testid="human-done" style={{ borderColor: "var(--accent)" }}>
                        <div className="mono" style={{ color: "var(--accent)", letterSpacing: "0.12em" }}>
                            {t("employees.finalCta.humanDoneStatus")}
                        </div>
                        <div style={{ marginTop: "0.5rem" }}>{t("employees.finalCta.humanDoneBody")}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ShareSectionUI() {
    const { t } = useTranslation();
    const [yourName, setYourName] = useState("");
    const [yourEmail, setYourEmail] = useState("");
    const [recipients, setRecipients] = useState([{ name: "", email: "" }]);
    const [note, setNote] = useState("");
    const [sent, setSent] = useState(null); // { count } on success
    const [err, setErr] = useState("");
    const [badIdx, setBadIdx] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const MAX = SHARE_MAX_FRIEND_RECIPIENTS;

    const update = (i, field) => (e) => {
        setRecipients((r) => {
            const next = [...r];
            next[i] = { ...next[i], [field]: e.target.value };
            return next;
        });
    };
    const add = () => {
        if (recipients.length >= MAX) return;
        setRecipients((r) => [...r, { name: "", email: "" }]);
    };
    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBadIdx([]);
        const valid = recipients.filter((r) => r.email.trim());
        if (!yourName.trim() || !yourEmail.trim()) {
            setErr(t("errors.allRequired"));
            return;
        }
        if (!isValidEmail(yourEmail.trim())) {
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        if (valid.length === 0) {
            setErr(t("employees.share.needRecipient"));
            return;
        }
        const firstBadRecipient = valid.findIndex((r) => !isValidEmail(r.email.trim()));
        if (firstBadRecipient >= 0) {
            setBadIdx([firstBadRecipient]);
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        track("share_friend_submit", { recipient_count: valid.length });
        setSubmitting(true);
        try {
            const res = await shareFriend({
                senderName: yourName.trim(),
                senderEmail: yourEmail.trim(),
                recipients: valid.map((r) => ({
                    name: r.name.trim() || undefined,
                    email: r.email.trim(),
                })),
                note: note.trim() || undefined,
            });
            track("share_friend_submit.success", {
                share_id: res.shareId,
                sent_count: res.sentCount,
            });
            setSent({ count: res.sentCount });
        } catch (e2) {
            track("share_friend_submit.failure", {});
            const indices = badRecipientIndices(e2);
            if (indices.length) {
                setBadIdx(indices);
                setErr(t("employees.share.badRecipients"));
            } else {
                setErr(formatApiError(e2) || t("errors.generic"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="share-section" data-testid="share-section">
            <div className="share-header">
                <div className="section-label mono">{t("employees.share.label")}</div>
                <h3><I18nText i18nKey="employees.share.h3" /></h3>
                <p>{t("employees.share.body")}</p>
            </div>

            {sent ? (
                <div data-testid="share-sent" style={{ padding: "1rem 0" }}>
                    <div className="mono" style={{ color: "var(--accent)", letterSpacing: "0.12em", marginBottom: "1rem" }}>
                        {t("employees.share.sent.status")}
                    </div>
                    <h4 style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: "2rem", lineHeight: 1.05, letterSpacing: "-0.02em", marginBottom: "1rem" }}>
                        <I18nText i18nKey="employees.share.sent.headline" values={{ count: sent.count }} />
                    </h4>
                    <p style={{ color: "var(--muted)", lineHeight: 1.6 }}>{t("employees.share.sent.body")}</p>
                    <div className="share-compliance" style={{ marginTop: "1rem" }}>{t("employees.share.compliance")}</div>
                </div>
            ) : (
                <form className="share-form" onSubmit={submit}>
                    <div className="share-field">
                        <label className="mono">{t("employees.share.labelYourName")}</label>
                        <input type="text" value={yourName} onChange={(e) => setYourName(e.target.value)}
                               onBlur={(e) => setYourName(titleCaseName(e.target.value))}
                               placeholder={t("employees.share.placeholderYourName")} data-testid="share-your-name" />
                    </div>
                    <div className="share-field">
                        <label className="mono">{t("employees.share.labelYourEmail")}</label>
                        <input type="email" value={yourEmail} onChange={(e) => setYourEmail(e.target.value)}
                               onBlur={(e) => setYourEmail(e.target.value.trim())}
                               placeholder={t("employees.share.placeholderYourEmail")} data-testid="share-your-email" />
                    </div>
                    {recipients.map((r, i) => (
                        <div className={`share-recipient${badIdx.includes(i) ? " is-bad" : ""}`} key={i}>
                            <div className="share-field">
                                <label className="mono">{t("employees.share.labelTheirName")}</label>
                                <input type="text" placeholder={t("employees.share.placeholderTheirName")}
                                       value={r.name} onChange={update(i, "name")}
                                       onBlur={(e) => setRecipients((rs) => rs.map((x, idx) => idx === i ? { ...x, name: titleCaseName(e.target.value) } : x))}
                                       data-testid={`rec-name-${i}`} />
                            </div>
                            <div className="share-field">
                                <label className="mono">{t("employees.share.labelTheirEmail")}</label>
                                <input type="email" placeholder={t("employees.share.placeholderTheirEmail")}
                                       value={r.email} onChange={update(i, "email")}
                                       onBlur={(e) => setRecipients((rs) => rs.map((x, idx) => idx === i ? { ...x, email: e.target.value.trim() } : x))}
                                       data-testid={`rec-email-${i}`} />
                            </div>
                        </div>
                    ))}
                    {recipients.length < MAX && (
                        <button type="button" className="btn-add-more" onClick={add} data-testid="share-add-more">
                            {t("employees.share.addMore")}
                        </button>
                    )}
                    <div className="share-field">
                        <label className="mono">{t("employees.share.labelNote")}</label>
                        <textarea rows={2} placeholder={t("employees.share.placeholderNote")}
                                  value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
                                  data-testid="share-note" />
                    </div>
                    {err && <div className="apply-err" data-testid="share-err">{err}</div>}
                    <button type="submit" className="btn-share" disabled={submitting} data-testid="share-submit">
                        {submitting ? t("employees.share.submitting") : t("employees.share.submit")}
                    </button>
                    <div className="share-compliance">{t("employees.share.compliance")}</div>
                </form>
            )}
        </div>
    );
}

/* Phase 3b §4 — three marketing CTAs for unauthed `/employees`. */
function EmployeesMarketingCTAs({ locale }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Phase 3d — "Send me the good stuff" replaces the Phase 3b §4 waitlist
    // card on this page. The old endpoint + `marketing_waitlist` collection
    // are frozen as read-only history (nothing writes to them anymore); the
    // canonical flow is now `/api/prospective-employees/capture` which both
    // stores the email AND immediately sends a content email via Mailgun.
    //
    // Card states:
    //   collapsed (idle)  → "Send me the good stuff →" button, nothing else
    //   expanded (open)   → email field + submit + disclosure + dismiss
    //   sent     (thanks) → warm confirmation, no action required
    const [openForm, setOpenForm] = useState(false);
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const openCapture = () => {
        if (sent) return;
        track("good_stuff_open", {});
        setOpenForm(true);
        setErr("");
    };

    const dismissCapture = () => {
        track("good_stuff_dismiss", {});
        setOpenForm(false);
        setErr("");
    };

    const submitGoodStuff = async (e) => {
        e.preventDefault();
        const v = email.trim();
        if (!isValidEmail(v)) {
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        setErr("");
        setSubmitting(true);
        track("good_stuff_submit", {});
        try {
            await sendMeTheGoodStuff({
                email: v,
                locale,
                referrer: typeof document !== "undefined" ? document.referrer || null : null,
            });
            track("good_stuff_submit.success", {});
            setSent(true);
        } catch (e2) {
            track("good_stuff_submit.failure", {});
            setErr(formatApiError(e2) || t("errors.generic"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="ee-marketing-ctas" data-testid="ee-marketing-ctas">
            <h3 className="ee-mk-h3">{t("employees.marketing.h3")}</h3>
            <div className="ee-mk-grid">
                <a
                    href="/apply"
                    className="ee-mk-card ee-mk-card-primary"
                    data-testid="ee-mk-signin"
                    onClick={() => track("employees_mk_signin_click", {})}
                >
                    <div className="mono ee-mk-label">{t("employees.marketing.signin.label")}</div>
                    <div className="ee-mk-title">{t("employees.marketing.signin.title")}</div>
                    <div className="ee-mk-cta">{t("employees.marketing.signin.cta")} →</div>
                </a>
                {/* id="employees-waitlist" retained for backward-compat
                    with any in-product deep links / external references. */}
                <div className="ee-mk-card" id="employees-waitlist" data-testid="ee-mk-goodstuff">
                    <div className="mono ee-mk-label">{t("employees.marketing.goodStuff.label")}</div>
                    <div className="ee-mk-title">{t("employees.marketing.goodStuff.title")}</div>
                    {sent ? (
                        <div className="ee-mk-ok mono" data-testid="ee-mk-goodstuff-sent">
                            {t("employees.marketing.goodStuff.sent")}
                        </div>
                    ) : openForm ? (
                        <form className="ee-mk-form" onSubmit={submitGoodStuff}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={(e) => setEmail(e.target.value.trim())}
                                placeholder={t("employees.marketing.goodStuff.placeholder")}
                                data-testid="ee-mk-goodstuff-email"
                                autoFocus
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="ee-mk-goodstuff-submit"
                            >
                                {submitting
                                    ? t("employees.marketing.goodStuff.submitting")
                                    : t("employees.marketing.goodStuff.submit")}
                            </button>
                            {err && (
                                <div className="apply-err" data-testid="ee-mk-goodstuff-err">
                                    {err}
                                </div>
                            )}
                            <div className="ee-mk-goodstuff-disclosure">
                                {t("employees.marketing.goodStuff.disclosure")}
                            </div>
                            <button
                                type="button"
                                className="ee-mk-goodstuff-dismiss"
                                onClick={dismissCapture}
                                data-testid="ee-mk-goodstuff-dismiss"
                            >
                                {t("employees.marketing.goodStuff.dismiss")}
                            </button>
                        </form>
                    ) : (
                        <button
                            type="button"
                            className="ee-mk-goodstuff-openbtn"
                            onClick={openCapture}
                            data-testid="ee-mk-goodstuff-open"
                        >
                            {t("employees.marketing.goodStuff.cta")} →
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    className="ee-mk-card"
                    onClick={() => {
                        track("employees_mk_nominate_click", {});
                        navigate("/nominate");
                    }}
                    data-testid="ee-mk-nominate"
                >
                    <div className="mono ee-mk-label">{t("employees.marketing.nominate.label")}</div>
                    <div className="ee-mk-title">{t("employees.marketing.nominate.title")}</div>
                    <div className="ee-mk-cta">{t("employees.marketing.nominate.cta")} →</div>
                </button>
            </div>
        </div>
    );
}



export default function Employees() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        track("employee_view", { hasInvite: false });
        document.title = t("employees.title");
    }, [t]);

    const guarantees = t("employees.guarantees.items", { returnObjects: true }) || [];
    const portSteps = t("employees.portability.steps", { returnObjects: true }) || [];

    return (
        <section className="lg-section track" data-testid="employee-track">
            <button
                className="back-link"
                onClick={(e) => { e.preventDefault(); navigate("/"); }}
                data-testid="employee-back"
            >
                {t("employees.back")}
            </button>

            <div className="ee-hero">
                <div className="section-label mono">{t("employees.sectionLabel")}</div>
                <h2><I18nText i18nKey="employees.h2" /></h2>
                <p className="ee-hero-sub">{t("employees.sub")}</p>
            </div>

            <Calculator invited={null} />

            <div className="section-label mono">{t("employees.guarantees.label")}</div>
            <div className="guarantees" data-testid="guarantees">
                {guarantees.map((g, i) => (
                    <div className="guar" key={i}>
                        <div>
                            <div className="guar-tag mono">{g.tag}</div>
                            <h4>{g.h4}</h4>
                        </div>
                        <p>{g.body}</p>
                    </div>
                ))}
            </div>

            {/* Phase 3c polish — "Who's this for?" cards moved here, above
                the long-form story sections. Anchored with id so the
                "Start my application" CTA below can scroll back up to them. */}
            <div id="whos-this-for" data-testid="whos-this-for-anchor">
                <EmployeesMarketingCTAs locale={i18n.language || "en"} />
            </div>

            {/* Phase 3b §4 — shared Charles story replaces the old `story` block.
                Same source as employee portal phase 1 Section D. */}
            <div className="ee-charles-section" data-testid="ee-charles-section">
                <CharlesStory />
            </div>

            <div className="port">
                <div className="port-left">
                    <div className="section-label mono">{t("employees.portability.label")}</div>
                    <h3><I18nText i18nKey="employees.portability.h3" /></h3>
                    <p>{t("employees.portability.body")}</p>
                </div>
                <div className="port-visual">
                    {portSteps.map((s, i) => (
                        <div className="port-step" key={i}>
                            <div className="port-step-num">{s.num}</div>
                            <div className="port-step-text">
                                <strong>{s.strong}</strong>
                                {s.body}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Phase 3d follow-up — previously this CTA anchored back up to
                #whos-this-for to let the user pick a path from the 3-card
                block. Per Nigel: the button should just take the user
                straight to the employee email-lookup at `/apply`, same
                as the calculator's "I WANT THIS" CTA. One decision, one
                surface. */}
            <div className="ee-get-covered-wrap" data-testid="ee-get-covered-wrap">
                <a
                    href="/apply"
                    className="btn-get-covered"
                    data-testid="ee-get-covered"
                    onClick={() => track("employees_get_covered_click", { path: "portability_startapp" })}
                >
                    {t("employees.getCovered")}
                </a>
            </div>
        </section>
    );
}
